import { useState, useEffect, lazy, Suspense, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildTrackingUrl } from '@/lib/marketing';
import { CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import { useSubscriptionWithTier } from '@/hooks/useSubscription';
import { createCheckoutSession } from '@/lib/subscription';
import { useToast } from "@/hooks/use-toast";
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { useDynamicPricing } from '@/hooks/useDynamicPricing';
import { BillingToggle } from '@/components/pricing/BillingToggle';
import { useCountdown } from '@/hooks/useCountdown';
import { logger } from '@/lib/logger';
import { paymentApi } from '@/lib/payment-api';

// Lazy load Stripe checkout dialog to avoid loading Stripe.js on homepage
const StripeCheckoutDialog = lazy(() =>
  import('@/components/stripe/StripeCheckoutDialog').then(module => ({
    default: module.StripeCheckoutDialog
  }))
);

interface HomepagePricingSectionProps {
  className?: string;
}

export function HomepagePricingSection({ className = '' }: HomepagePricingSectionProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  useSubscriptionWithTier();
  const [, setIsLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentTierName, setCurrentTierName] = useState<string | null>(null);
  const [isGuestCheckout, setIsGuestCheckout] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');
  const [isFetchingClientSecret, setIsFetchingClientSecret] = useState(false);
  const { toast } = useToast();

  // Use refs to track state for closure-safe callbacks
  const isFetchingRef = useRef(false);
  const clientSecretRef = useRef<string | null>(null);

  // Keep refs in sync with state
  useEffect(() => {
    isFetchingRef.current = isFetchingClientSecret;
  }, [isFetchingClientSecret]);

  useEffect(() => {
    clientSecretRef.current = clientSecret;
  }, [clientSecret]);

  // Use dynamic pricing hook with billing interval support
  const {
    pricingTiers,
    masterFeatures,
    billingInterval,
    setBillingInterval,
    hasAnnualPricing,
    getTierPricing
  } = useDynamicPricing();

  // Get savings for display (memoized to prevent recalculation on every render)
  const unlimitedPricing = useMemo(() => getTierPricing('Unlimited'), [getTierPricing]);

  // Countdown to February 15, 2026 at midnight EST (price increase date)
  const priceIncreaseDate = new Date('2026-02-15T05:00:00Z'); // Midnight EST = 5:00 UTC
  const { days, hours, minutes, seconds, isExpired } = useCountdown(priceIncreaseDate);

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setClientSecret(null);
    setCurrentTierName(null);
    setIsGuestCheckout(false);
    setGuestEmail('');
    setIsFetchingClientSecret(false);
  };

  const initiateSubscription = async (tierName: string) => {
    try {
      setIsLoading(true);
      setCurrentTierName(tierName);

      // Show modal immediately with loading state for better UX
      setShowPaymentModal(true);

      logger.debug(`Initiating subscription for tier: ${tierName}, interval: ${billingInterval}`);

      const redirectUrlOrClientSecret = await createCheckoutSession(user?.id, tierName.toLowerCase(), billingInterval);
      logger.debug('Response from createCheckoutSession:', { response: redirectUrlOrClientSecret });

      if (redirectUrlOrClientSecret) {
        // Check if the result is a URL or a client secret
        if (redirectUrlOrClientSecret.startsWith('/checkout-confirmation')) {
          logger.debug('Received checkout-confirmation URL, redirecting...');
          setShowPaymentModal(false); // Close modal before redirect
          window.location.href = redirectUrlOrClientSecret;
        } else if (redirectUrlOrClientSecret.startsWith('http')) {
          logger.debug('Received direct URL, redirecting...');
          setShowPaymentModal(false); // Close modal before redirect
          window.location.href = redirectUrlOrClientSecret;
        } else {
          logger.debug('Received client secret, updating modal...');
          setClientSecret(redirectUrlOrClientSecret);
          // Modal is already open, just update with client secret
        }
      } else {
        logger.error('No response from createCheckoutSession');
        setShowPaymentModal(false); // Close modal on error
        toast({
          title: 'Error',
          description: 'Failed to create checkout session. Please try again later.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      logger.error('Error creating checkout session:', error);

      setShowPaymentModal(false); // Close modal on error

      // Show more detailed error to the user
      let errorMessage = 'Failed to create checkout session. Please try again later.';
      if (error instanceof Error) {
        errorMessage = `Error: ${error.message}`;
      }

      toast({
        title: 'Checkout Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Guest checkout flow - for users without an account
  const initiateGuestCheckout = async (tierName: string) => {
    try {
      setIsLoading(true);
      setCurrentTierName(tierName);
      setIsGuestCheckout(true);

      // Show modal immediately - user will enter email in the modal
      setShowPaymentModal(true);

      logger.debug(`Initiating guest checkout for tier: ${tierName}, interval: ${billingInterval}`);

      // We don't call the edge function yet - we'll wait for the user to enter their email
      // The client secret will be fetched when they submit the form
      // For now, leave clientSecret as null to show the email input first

    } catch (error) {
      logger.error('Error initiating guest checkout:', error);
      setShowPaymentModal(false);
      setIsGuestCheckout(false);

      toast({
        title: 'Checkout Error',
        description: 'Failed to start checkout. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch client secret for guest checkout when email is provided
  const fetchGuestClientSecret = async (email: string) => {
    try {
      setIsLoading(true);
      setIsFetchingClientSecret(true);
      isFetchingRef.current = true; // Update ref immediately
      logger.debug(`Fetching guest client secret for email: ${email.substring(0, 3)}***`);

      const { data, error, errorData: errBody } = await paymentApi('/pay/guest/create', {
          email,
          tier: 'unlimited',
          interval: billingInterval,
      }, { auth: false });

      logger.debug('create-guest-payment response:', { data, error, hasData: !!data, hasError: !!error });

      if (error) {
        logger.debug('Error from create-guest-payment:', { message: error });

        // Handle EMAIL_EXISTS error
        if (errBody?.code === 'EMAIL_EXISTS') {
          toast({
            title: 'Account Exists',
            description: 'An account with this email already exists. Please log in instead.',
            variant: 'destructive',
          });
          handleClosePaymentModal();
          navigate('/auth?tab=login&redirectTo=/pricing');
          return;
        }

        throw new Error(errBody?.detail || error || 'Failed to create payment session');
      }

      if (data?.error) {
        // Handle specific error codes (for 2xx responses with error in body)
        if (data.code === 'EMAIL_EXISTS') {
          toast({
            title: 'Account Exists',
            description: 'An account with this email already exists. Please log in instead.',
            variant: 'destructive',
          });
          handleClosePaymentModal();
          navigate('/auth?tab=login&redirectTo=/pricing');
          return;
        }
        throw new Error(data.detail || data.error);
      }

      if (data?.client_secret) {
        logger.debug('Received guest client secret');
        setClientSecret(data.client_secret);
        clientSecretRef.current = data.client_secret; // Update ref immediately
      } else {
        throw new Error('No client secret received');
      }
    } catch (error) {
      logger.error('Error fetching guest client secret:', error);

      let errorMessage = 'Failed to process checkout. Please try again.';
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast({
        title: 'Checkout Error',
        description: errorMessage,
        variant: 'destructive',
      });

      handleClosePaymentModal();
    } finally {
      setIsLoading(false);
      setIsFetchingClientSecret(false);
      isFetchingRef.current = false; // Reset ref
    }
  };

  // Handle email change in guest checkout - fetch client secret when valid email entered
  // Using refs to check state values to avoid stale closure issues
  const handleGuestEmailChange = (email: string) => {
    logger.debug('handleGuestEmailChange called with:', { email: email.substring(0, 3) + '***', clientSecretRef: !!clientSecretRef.current, isFetchingRef: isFetchingRef.current });
    setGuestEmail(email);

    // If email is valid and we don't have a client secret yet, fetch it
    // Use refs to check the latest values (avoid stale closure)
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (isValidEmail && !clientSecretRef.current && !isFetchingRef.current) {
      logger.debug('Triggering fetchGuestClientSecret');
      fetchGuestClientSecret(email);
    } else {
      logger.debug('Not fetching client secret:', { isValidEmail, hasClientSecret: !!clientSecretRef.current, isFetching: isFetchingRef.current });
    }
  };

  const handleSubscribe = (tierName: string) => {
    // Free tier: redirect to signup or chat
    if (tierName === 'Free') {
      if (!user) {
        navigate(buildTrackingUrl('/auth?tab=signup', { utm_campaign: 'homepage_pricing', utm_content: 'free_tier_signup' }));
      } else {
        navigate(buildTrackingUrl('/chat', { utm_campaign: 'homepage_pricing', utm_content: 'free_tier_chat' }));
      }
      return;
    }

    // Paid tier: if logged in, open payment modal directly
    if (user) {
      initiateSubscription(tierName);
      return;
    }

    // Not logged in: open guest checkout modal
    initiateGuestCheckout(tierName);
  };

  return (
    <>
      {/* Beta pricing countdown - ABOVE cards */}
      {!isExpired && (
        <div className="max-w-3xl mx-auto mb-10 text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            Current pricing ends:
          </p>

          <div className="flex justify-center gap-3 md:gap-4 mb-4">
            <div className="flex flex-col items-center">
              <div className="bg-gray-100 dark:bg-gray-800 rounded-lg px-4 py-3 min-w-[4rem]">
                <span className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{days}</span>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">days</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-gray-100 dark:bg-gray-800 rounded-lg px-4 py-3 min-w-[4rem]">
                <span className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{hours.toString().padStart(2, '0')}</span>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">hours</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-gray-100 dark:bg-gray-800 rounded-lg px-4 py-3 min-w-[4rem]">
                <span className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{minutes.toString().padStart(2, '0')}</span>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">min</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-gray-100 dark:bg-gray-800 rounded-lg px-4 py-3 min-w-[4rem]">
                <span className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{seconds.toString().padStart(2, '0')}</span>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">sec</span>
            </div>
          </div>

          <p className="text-sm text-gray-500 dark:text-gray-400">
            Starting February 15: <span className="font-semibold">$30/month</span> or <span className="font-semibold">$300/year</span>
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Subscribe now to lock in the current rate.
          </p>
        </div>
      )}

      {/* Billing toggle - below countdown, above cards */}
      {hasAnnualPricing && (
        <div className="max-w-3xl mx-auto mb-8">
          <BillingToggle
            value={billingInterval}
            onChange={setBillingInterval}
            freeMonths={unlimitedPricing.savings.freeMonths}
          />
        </div>
      )}

      {/* Unified Card View for all screen sizes - Homepage adapted */}
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto ${className}`}>
        {pricingTiers.map((tier) => {
          const TierIcon = tier.icon;
          return (
            <div
              key={tier.name}
              className={`relative flex flex-col p-8 rounded-2xl shadow-xl transition-all duration-300 hover:scale-[1.03]
                ${tier.highlight
                  ? 'bg-orange-500 text-white ring-2 ring-orange-600'
                  : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700'
                }
              `}
            >
              {tier.highlight && billingInterval === 'year' && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-orange-600 text-white px-4 py-1 rounded-full text-xs font-semibold shadow-md whitespace-nowrap">
                    MOST POPULAR
                  </span>
                </div>
              )}

              <div className="flex items-center gap-3 mb-4">
                <TierIcon className={`w-7 h-7 ${tier.highlight ? 'text-white' : 'text-orange-500'}`} />
                <h3 className={`text-2xl font-bold ${tier.highlight ? 'text-white' : 'text-gray-900 dark:text-white'}`}>{tier.name}</h3>
              </div>
              <div className="mb-2">
                <span className={`text-3xl font-extrabold ${tier.highlight ? 'text-white' : 'text-gray-900 dark:text-white'}`}>{tier.price}</span>
                <span className={`text-sm ${tier.highlight ? 'text-orange-100' : 'text-gray-500 dark:text-gray-400'}`}>/{billingInterval === 'year' && tier.name !== 'Free' ? 'year' : 'month'}</span>
              </div>
              <p className={`text-sm mb-4 min-h-[40px] ${tier.highlight ? 'text-orange-50' : 'text-gray-600 dark:text-gray-400'}`}>
                {tier.description}
              </p>

              {/* Divider before features */}
              <div className={`border-t mb-5 ${tier.highlight ? 'border-white/40' : 'border-gray-200 dark:border-gray-700'}`}></div>

              <ul className="space-y-4 mb-6 flex-grow">
                {masterFeatures.map((feature) => {
                  const tierFeatureInfo = tier.features[feature.id];

                  // For chat messages feature, display the complete text with limits included
                  if (feature.id === 'chat_messages') {
                    return (
                      <li key={`${tier.name}-${feature.id}`} className="flex items-start gap-2.5">
                        {tierFeatureInfo?.included ? (
                          <CheckCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${tier.highlight ? 'text-white' : 'text-green-500'}`} />
                        ) : (
                          <XCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${tier.highlight ? 'text-white/40' : 'text-gray-400'}`} />
                        )}
                        <span className={`${tier.highlight ? 'text-white/95' : 'text-gray-700 dark:text-gray-300'} ${!tierFeatureInfo?.included ? 'line-through opacity-60' : ''}`}>
                          {tier.name === 'Free' ? '10 AI chat messages per month' : 'Unlimited AI chat messages'}
                        </span>
                      </li>
                    );
                  }

                  // Regular feature rendering for all other features
                  return (
                    <li key={`${tier.name}-${feature.id}`} className="flex items-start gap-2.5">
                      {tierFeatureInfo?.included ? (
                        <CheckCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${tier.highlight ? 'text-white' : 'text-green-500'}`} />
                      ) : (
                        <XCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${tier.highlight ? 'text-white/40' : 'text-gray-400'}`} />
                      )}
                      <span className={`${tier.highlight ? 'text-white/95' : 'text-gray-700 dark:text-gray-300'} ${!tierFeatureInfo?.included ? 'line-through opacity-60' : ''}`}>
                        {feature.displayName}{tierFeatureInfo?.value === 'Limited' ? ' (Limited)' : ''}
                      </span>
                    </li>
                  );
                })}
              </ul>

              <Button
                onClick={() => handleSubscribe(tier.name)}
                size="lg"
                className={`w-full mt-auto ${tier.highlight
                  ? 'bg-white text-orange-600 hover:bg-gray-100'
                  : 'bg-orange-500 hover:bg-orange-600 text-white'
                  }`}
              >
                {tier.name === 'Free' ? 'Get started' : 'Lock in current pricing'}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Simple note below cards */}
      <div className="text-center mt-6">
        <p className="text-gray-500 dark:text-gray-400 text-sm">
          Cancel anytime. No commitments.
        </p>
      </div>

      {/* Payment modal - Lazy loaded to avoid loading Stripe.js until needed */}
      {showPaymentModal && (
        <Suspense fallback={<LoadingSpinner />}>
          <StripeCheckoutDialog
            open={showPaymentModal}
            onClose={handleClosePaymentModal}
            clientSecret={clientSecret || 'loading'} // Use 'loading' as placeholder when no client secret yet
            title={isGuestCheckout ? 'Start your subscription' : `Complete your ${currentTierName} subscription`}
            description={isGuestCheckout ? 'Enter your email to create an account and start your subscription.' : 'This will give you access to Ask JDS premium features.'}
            tier={currentTierName}
            isGuestCheckout={isGuestCheckout}
            guestEmail={guestEmail}
            onGuestEmailChange={handleGuestEmailChange}
            isFetchingClientSecret={isFetchingClientSecret}
            onSuccess={(paymentIntentId) => {
              logger.debug('Payment successful from homepage', { paymentIntentId, isGuestCheckout });
            }}
            onError={(error) => {
              logger.error('Payment error:', error);
              toast({
                title: "Error",
                description: error.message || 'Payment failed',
                variant: "destructive",
              });
            }}
          />
        </Suspense>
      )}
    </>
  );
} 